import React from "react";
import { z } from "zod";

import { FormDateRangePicker, FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import EmployeePicker, { type EmployeeOption } from "../../../../components/EmployeePicker";
import { SingleEmployeeSchema } from "../../../../components/EmployeePicker/utils";
import Spacer from "../../../../components/Spacer";

import type { WorkspacesFilters } from "../schemas";

import { capitalizeWord } from "../../../../utils/stringUtils";

import { workspacesTableConfig } from "../config";

const DateRangeFilterSchema = z
	.object({
		from: z.date().optional(),
		to: z.date().optional(),
	})
	.optional();

const WorkspacesFilterPanelSchema = z.object({
	createdBy: SingleEmployeeSchema.optional(),
	participant: SingleEmployeeSchema.optional(),
	previousMeetingRange: DateRangeFilterSchema,
	upcomingMeetingRange: DateRangeFilterSchema,
	status: z.enum(workspacesTableConfig.statusOptions).optional(),
});

const toDateRange = (from?: Date, to?: Date) => (from || to ? { from, to } : undefined);

const toSingleEmployee = (employee: EmployeeOption | EmployeeOption[] | null): WorkspacesFilters["createdBy"] => {
	const singleEmployee = Array.isArray(employee) ? employee[0] : employee;

	if (singleEmployee && "value" in singleEmployee) {
		return singleEmployee;
	}

	return undefined;
};

export const WorkspacesFilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: WorkspacesFilters) => void;
	filters: WorkspacesFilters;
}) => {
	const statusRadioOptions = workspacesTableConfig.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={WorkspacesFilterPanelSchema}
			defaultValues={{
				createdBy: filters.createdBy,
				participant: filters.participant,
				previousMeetingRange: toDateRange(filters.previousMeetingStart, filters.previousMeetingEnd),
				upcomingMeetingRange: toDateRange(filters.upcomingMeetingStart, filters.upcomingMeetingEnd),
				status: filters.status,
			}}
			onSubmit={({ value }) => {
				onApplyFilters({
					createdBy: value.createdBy,
					participant: value.participant,
					previousMeetingStart: value.previousMeetingRange?.from,
					previousMeetingEnd: value.previousMeetingRange?.to,
					upcomingMeetingStart: value.upcomingMeetingRange?.from,
					upcomingMeetingEnd: value.upcomingMeetingRange?.to,
					status: value.status,
				});
				onClose();
			}}
		>
			<FormField<WorkspacesFilters["createdBy"]>
				id="createdBy"
				label="Created By"
				input={(field) => (
					<EmployeePicker
						inputId="createdBy"
						value={field.value ?? ""}
						onChange={(newEmployeeValue) => field.onChange(toSingleEmployee(newEmployeeValue))}
						isClearable
					/>
				)}
			/>
			<Spacer height={2} />
			<FormField<WorkspacesFilters["participant"]>
				id="participant"
				label="Participant"
				input={(field) => (
					<EmployeePicker
						inputId="participant"
						value={field.value ?? ""}
						onChange={(newEmployeeValue) => field.onChange(toSingleEmployee(newEmployeeValue))}
						isClearable
					/>
				)}
			/>
			<Spacer height={2} />
			<FormDateRangePicker
				id="previousMeetingRange"
				label="Previous Meeting"
				popoverContentClassName="z-[1100]"
				menuAlign="start"
				hasClearButton
			/>
			<Spacer height={2} />
			<FormDateRangePicker
				id="upcomingMeetingRange"
				label="Upcoming Meeting"
				popoverContentClassName="z-[1100]"
				menuAlign="start"
				hasClearButton
			/>
			<Spacer height={2} />
			<FormRadioButtons id="status" label="Status" options={statusRadioOptions} />
		</PanelFilters>
	);
};

export default WorkspacesFilterPanel;
